import { Subscription } from 'rxjs';
import { jitsiService } from '@shared/singletons/jitsi-singleton';
import { Logger } from '@shared/services/logger.service';

/**
 * AV-moderation approval for freshly promoted speakers (NGE-1078).
 *
 * An auditorium runs with AV moderation on, so only Jitsi moderators may publish —
 * a SPEAKER token is force-muted until the moderator approves that participant.
 * Approving is not a one-shot call though: the promoted client has to re-join to
 * pick up its new token, and it comes back with a **new Jitsi participant id**, so
 * an approval sent before the re-join lands on an endpoint that no longer exists.
 * Their camera and screen share then look fine locally while everyone else sees a
 * black tile.
 *
 * So the moderator queues the promotion by identity (user pid for a member, guest
 * pid for a guest — both survive the re-join, they come from the JWT) and re-applies
 * the approval whenever that identity shows up under a new participant id.
 */

/** How long to keep re-applying an approval after the promotion. */
const APPROVAL_WINDOW_MS = 90000;

interface PendingApproval {
  /** The participant id we last approved, so the same id isn't re-approved. */
  approvedParticipantId?: string;
  expiresAt: number;
}

const pending = new Map<string, PendingApproval>();
let rosterSubscription: Subscription | null = null;

const stopWatching = () => {
  rosterSubscription?.unsubscribe();
  rosterSubscription = null;
};

/** Approve every pending identity that is currently in the conference. */
const approvePending = () => {
  const now = Date.now();
  for (const [pid, entry] of pending) {
    if (entry.expiresAt <= now) {
      pending.delete(pid);
      continue;
    }
    const participantId = jitsiService
      ?.getParticipants?.()
      .find((participant) => participant.userPid === pid)?.id;
    if (!participantId || participantId === entry.approvedParticipantId) continue;

    entry.approvedParticipantId = participantId;
    Logger.debug('[Auditorium] Approving promoted speaker', pid, 'as', participantId);
    void jitsiService?.approveParticipantMedia(participantId);
  }
  if (pending.size === 0) stopWatching();
};

/**
 * Queue an approval for a promoted attendee and keep re-applying it across their
 * re-join. Safe to call for a moderator promotion too — a Jitsi moderator is
 * exempt from AV moderation, so the extra approve is a no-op.
 */
export const queueSpeakerApproval = (attendeePid: string): void => {
  pending.set(attendeePid, { expiresAt: Date.now() + APPROVAL_WINDOW_MS });

  // Roster changes (USER_JOINED / TRACK_ADDED / …) are exactly when a re-joined
  // participant becomes addressable again.
  if (!rosterSubscription) {
    rosterSubscription = jitsiService?.events$.subscribe(() => approvePending()) ?? null;
  }
  approvePending();
};

/** Forget a queued approval — the attendee was demoted again. */
export const cancelSpeakerApproval = (attendeePid: string): void => {
  pending.delete(attendeePid);
  if (pending.size === 0) stopWatching();
};

/** Drop everything (auditorium left / room switched). */
export const clearSpeakerApprovals = (): void => {
  pending.clear();
  stopWatching();
};
